import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle, Loader2, AlertTriangle, Building2 } from 'lucide-react';
import { authClient, useSession, organizationApi, signOut } from '@/lib/auth-client';
import { AuthLayout } from '@/components/auth/auth-layout';
import { PasswordInput } from '@/components/auth/password-input';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api-client';

export const Route = createFileRoute('/accept-invitation/$token')({
  component: AcceptInvitationPage,
});

type PageStatus = 'loading' | 'join-form' | 'accepting' | 'success' | 'expired' | 'error';

interface InvitationInfo {
  email: string;
  workspaceName: string;
  status: string;
  userExists: boolean;
}

const signUpSchema = z.object({
  name: z.string().min(2, 'nameMin').max(100, 'nameMax'),
  password: z.string().min(10, 'passwordMin').max(128, 'passwordMax'),
});

type SignUpForm = z.infer<typeof signUpSchema>;

function AcceptInvitationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = Route.useParams();
  const { data: session, isPending } = useSession();

  const [status, setStatus] = React.useState<PageStatus>('loading');
  const [errorMessage, setErrorMessage] = React.useState('');
  const [invitationInfo, setInvitationInfo] = React.useState<InvitationInfo | null>(null);
  const hasAttempted = React.useRef(false);

  // 1. Fetch public invitation info
  React.useEffect(() => {
    api
      .get<{ data: InvitationInfo }>(`/team/invitations/public/${token}`)
      .then((res) => {
        const info = res.data;
        setInvitationInfo(info);

        if (info.status === 'expired') {
          setStatus('expired');
        } else if (info.status !== 'pending') {
          setStatus('error');
          setErrorMessage(t('invitation.alreadyUsed'));
        }
      })
      .catch(() => {
        setStatus('error');
        setErrorMessage(t('invitation.errorGeneric'));
      });
  }, [token, t]);

  const acceptInvitation = React.useCallback(async () => {
    setStatus('accepting');

    const result = await organizationApi.acceptInvitation({ invitationId: token });

    if (result.error) {
      const msg = result.error.message ?? '';
      if (msg.toLowerCase().includes('not the recipient')) {
        // Wrong account — sign out and show join form
        await signOut();
        hasAttempted.current = false;
        setStatus('join-form');
        return;
      }

      // BA may have auto-accepted during signUp — check if user is already in a workspace
      const orgs = await authClient.organization.list();
      if (orgs.data && orgs.data.length > 0) {
        setStatus('success');
        setTimeout(() => {
          navigate({ to: '/dashboard' as '/' });
        }, 3000);
        return;
      }

      setStatus('error');
      setErrorMessage(t('invitation.errorGeneric'));
      return;
    }

    setStatus('success');
    setTimeout(() => {
      navigate({ to: '/dashboard' as '/' });
    }, 3000);
  }, [token, navigate, t]);

  // 2. Once we have invitation info + session, decide what to do
  React.useEffect(() => {
    if (isPending || !invitationInfo) return;
    if (invitationInfo.status !== 'pending') return;

    // Not signed in → show join form
    if (!session?.user) {
      setStatus('join-form');
      return;
    }

    // Signed in → try to accept
    if (hasAttempted.current) return;
    hasAttempted.current = true;

    acceptInvitation();
  }, [isPending, session, invitationInfo, acceptInvitation]);

  const handleSuccess = React.useCallback(async () => {
    setStatus('success');
    // Force session refresh bypassing cookie cache — emailVerified was set server-side
    try {
      await authClient.getSession({ query: { disableCookieCache: true } });
    } catch {
      // Non-blocking
    }
    setTimeout(() => {
      navigate({ to: '/dashboard' as '/' });
    }, 3000);
  }, [navigate]);

  const handleError = React.useCallback((msg: string) => {
    setStatus('error');
    setErrorMessage(msg);
  }, []);

  // --- Loading / spinner ---
  if (status === 'loading' || status === 'accepting') {
    return (
      <AuthLayout
        title={t('invitation.title')}
        description={status === 'accepting' ? t('invitation.accepting') : t('invitation.checking')}
      >
        <div className="flex justify-center py-8">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </AuthLayout>
    );
  }

  // --- Success ---
  if (status === 'success') {
    return (
      <AuthLayout
        title={t('invitation.successTitle')}
        description={t('invitation.successDescription', {
          workspace: invitationInfo?.workspaceName,
        })}
      >
        <div className="flex flex-col items-center gap-4 py-6">
          <div className="flex size-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-950/50">
            <CheckCircle2 className="size-8 text-green-600 dark:text-green-400" />
          </div>
          <p className="text-sm text-muted-foreground">{t('invitation.redirecting')}</p>
        </div>
        <Button
          className="h-12 w-full cursor-pointer rounded-lg text-sm font-semibold"
          size="lg"
          onClick={() => navigate({ to: '/dashboard' as '/' })}
        >
          {t('invitation.goToDashboard')}
        </Button>
      </AuthLayout>
    );
  }

  // --- Join form (not signed in or wrong account) ---
  if (status === 'join-form') {
    const userExists = invitationInfo?.userExists ?? false;

    return (
      <AuthLayout
        title={t('invitation.joinTitle', { workspace: invitationInfo?.workspaceName })}
        description={
          userExists
            ? t('invitation.joinDescriptionExisting', {
                workspace: invitationInfo?.workspaceName,
              })
            : t('invitation.joinDescription', {
                workspace: invitationInfo?.workspaceName,
              })
        }
      >
        {userExists ? (
          <ExistingUserForm
            token={token}
            email={invitationInfo?.email ?? ''}
            workspaceName={invitationInfo?.workspaceName ?? ''}
            hasAttemptedRef={hasAttempted}
            onAccepting={() => setStatus('accepting')}
            onSuccess={handleSuccess}
            onError={handleError}
          />
        ) : (
          <NewUserForm
            token={token}
            email={invitationInfo?.email ?? ''}
            workspaceName={invitationInfo?.workspaceName ?? ''}
            hasAttemptedRef={hasAttempted}
            onAccepting={() => setStatus('accepting')}
            onSuccess={handleSuccess}
            onError={handleError}
          />
        )}
      </AuthLayout>
    );
  }

  // --- Expired ---
  if (status === 'expired') {
    return (
      <AuthLayout
        title={t('invitation.expiredTitle')}
        description={t('invitation.expiredDescription')}
      >
        <div className="flex flex-col items-center gap-4 py-6">
          <div className="flex size-16 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-950/50">
            <AlertTriangle className="size-8 text-amber-600 dark:text-amber-400" />
          </div>
        </div>
      </AuthLayout>
    );
  }

  // --- Generic error ---
  return (
    <AuthLayout
      title={t('invitation.errorTitle')}
      description={errorMessage || t('invitation.errorGeneric')}
    >
      <div className="flex flex-col items-center gap-4 py-6">
        <div className="flex size-16 items-center justify-center rounded-full bg-destructive/10">
          <XCircle className="size-8 text-destructive" />
        </div>
      </div>
      <Button
        className="h-12 w-full cursor-pointer rounded-lg text-sm font-semibold"
        size="lg"
        onClick={() => navigate({ to: '/' })}
      >
        {t('invitation.goToHome')}
      </Button>
    </AuthLayout>
  );
}

function InvitationHeader({ workspaceName }: { workspaceName: string }) {
  return (
    <div className="-mt-2 flex items-center gap-3 rounded-lg border bg-muted/50 p-3">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
        <Building2 className="size-5 text-primary" />
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium truncate">{workspaceName}</p>
      </div>
    </div>
  );
}

interface FormProps {
  token: string;
  email: string;
  workspaceName: string;
  hasAttemptedRef: React.MutableRefObject<boolean>;
  onAccepting: () => void;
  onSuccess: () => void;
  onError: (msg: string) => void;
}

/**
 * Form for existing users — no password needed.
 * Token from email is proof of identity. Calls backend to accept by token.
 */
function ExistingUserForm({
  token,
  email,
  workspaceName,
  onAccepting,
  onSuccess,
  onError,
}: FormProps) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleAccept = async () => {
    setIsSubmitting(true);

    try {
      onAccepting();

      await api.post(`/team/invitations/public/${token}/accept`);

      onSuccess();
    } catch {
      onError(t('invitation.errorGeneric'));
    }
  };

  return (
    <div className="space-y-4">
      <InvitationHeader workspaceName={workspaceName} />

      {/* Email — locked to invitation email */}
      <div className="space-y-2">
        <Label htmlFor="join-email">{t('auth.emailLabel')}</Label>
        <Input
          id="join-email"
          type="email"
          value={email}
          disabled
          className="h-12 rounded-lg bg-muted/50 text-sm text-muted-foreground"
        />
      </div>

      <Button
        type="button"
        className="mt-4 h-12 w-full cursor-pointer rounded-lg text-sm font-semibold"
        size="lg"
        disabled={isSubmitting}
        onClick={handleAccept}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            {t('invitation.joining')}
          </>
        ) : (
          t('invitation.joinButton')
        )}
      </Button>
    </div>
  );
}

/**
 * Form for new users — name + password, then signUp + acceptInvitation.
 */
function NewUserForm({
  token,
  email,
  workspaceName,
  hasAttemptedRef,
  onAccepting,
  onSuccess,
  onError,
}: FormProps) {
  const { t } = useTranslation();
  const [serverError, setServerError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [strengthScore, setStrengthScore] = React.useState(0);
  const [zxcvbnLoaded, setZxcvbnLoaded] = React.useState(false);
  const zxcvbnRef = React.useRef<typeof import('@zxcvbn-ts/core').zxcvbn | null>(null);

  // Lazy load zxcvbn
  React.useEffect(() => {
    let cancelled = false;
    async function loadZxcvbn() {
      const [core, commonPkg, plPkg] = await Promise.all([
        import('@zxcvbn-ts/core'),
        import('@zxcvbn-ts/language-common'),
        import('@zxcvbn-ts/language-pl'),
      ]);
      if (cancelled) return;
      core.zxcvbnOptions.setOptions({
        graphs: commonPkg.adjacencyGraphs,
        dictionary: {
          ...commonPkg.dictionary,
          ...plPkg.dictionary,
        },
        translations: plPkg.translations,
      });
      zxcvbnRef.current = core.zxcvbn;
      setZxcvbnLoaded(true);
    }
    loadZxcvbn();
    return () => {
      cancelled = true;
    };
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpForm>({
    resolver: zodResolver(signUpSchema),
    defaultValues: { name: '', password: '' },
  });

  const passwordValue = watch('password');

  // Password strength evaluation
  React.useEffect(() => {
    if (!passwordValue || passwordValue.length === 0) {
      setStrengthScore(0);
      return;
    }
    if (!zxcvbnRef.current) return;
    const timeout = setTimeout(() => {
      const result = zxcvbnRef.current!(passwordValue);
      const mapped = result.score <= 1 ? 1 : result.score <= 2 ? 2 : 3;
      setStrengthScore(mapped);
    }, 150);
    return () => clearTimeout(timeout);
  }, [passwordValue, zxcvbnLoaded]);

  const onSubmit = async (data: SignUpForm) => {
    setServerError(null);
    setIsSubmitting(true);
    hasAttemptedRef.current = true;

    try {
      // Step 1: Sign up (new user)
      const signUpResult = await authClient.signUp.email({
        email,
        password: data.password,
        name: data.name,
      });

      if (signUpResult.error) {
        const errorCode = signUpResult.error.code ?? '';
        const errorMsg = signUpResult.error.message ?? '';

        // Rate limit — show specific message, don't try signIn fallback
        if (errorCode === 'TOO_MANY_REQUESTS' || errorMsg.toLowerCase().includes('too many')) {
          setServerError(t('invitation.tooManyAttempts'));
          setIsSubmitting(false);
          return;
        }

        // Account might already exist — try sign in as fallback
        const signInResult = await authClient.signIn.email({
          email,
          password: data.password,
        });

        if (signInResult.error) {
          const signInCode = signInResult.error.code ?? '';
          const signInMsg = signInResult.error.message ?? '';

          if (signInCode === 'TOO_MANY_REQUESTS' || signInMsg.toLowerCase().includes('too many')) {
            setServerError(t('invitation.tooManyAttempts'));
          } else {
            setServerError(t('invitation.authError'));
          }
          setIsSubmitting(false);
          return;
        }
      }

      // Step 2: Accept the invitation.
      // Better Auth may auto-accept during signUp (if email matches invitation),
      // so acceptInvitation can fail with "not found" — treat that as success.
      onAccepting();

      const acceptResult = await organizationApi.acceptInvitation({
        invitationId: token,
      });

      if (acceptResult.error) {
        // Check if user is already in the workspace (BA auto-accepted during signUp)
        const orgs = await authClient.organization.list();
        if (orgs.data && orgs.data.length > 0) {
          onSuccess();
          return;
        }
        onError(t('invitation.errorGeneric'));
        return;
      }

      onSuccess();
    } catch {
      setServerError(t('invitation.errorGeneric'));
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
      <InvitationHeader workspaceName={workspaceName} />

      {/* Email — locked to invitation email */}
      <div className="space-y-2">
        <Label htmlFor="join-email">{t('auth.emailLabel')}</Label>
        <Input
          id="join-email"
          type="email"
          value={email}
          disabled
          className="h-12 rounded-lg bg-muted/50 text-sm text-muted-foreground"
        />
      </div>

      {/* Name */}
      <div className="space-y-2">
        <Label htmlFor="join-name">{t('onboarding.nameLabel')}</Label>
        <Input
          id="join-name"
          placeholder={t('onboarding.namePlaceholder')}
          autoComplete="name"
          autoFocus
          className="h-12 rounded-lg bg-background text-sm"
          aria-invalid={!!errors.name}
          {...register('name')}
        />
        {errors.name && (
          <p className="text-sm text-destructive">
            {t(`onboarding.validation.${errors.name.message}`)}
          </p>
        )}
      </div>

      {/* Password */}
      <div className="space-y-2">
        <Label htmlFor="join-password">{t('auth.passwordLabel')}</Label>
        <PasswordInput
          id="join-password"
          placeholder={t('auth.passwordPlaceholder')}
          autoComplete="new-password"
          className="h-12 rounded-lg bg-background text-sm"
          aria-invalid={!!errors.password}
          showStrength
          strengthScore={strengthScore}
          value={passwordValue}
          {...register('password')}
        />
        {errors.password && (
          <p className="text-sm text-destructive">
            {t(`auth.validation.${errors.password.message}`)}
          </p>
        )}
      </div>

      {serverError && <p className="text-sm text-destructive">{serverError}</p>}

      <Button
        type="submit"
        className="mt-2 h-12 w-full cursor-pointer rounded-lg text-sm font-semibold"
        size="lg"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            {t('invitation.joining')}
          </>
        ) : (
          t('invitation.joinButton')
        )}
      </Button>
    </form>
  );
}
